// Şub 27, 2026 — DPD Analitik & İçgörü (Fast-Trans özel)
// Comprehensive analytics: YoY, Monthly, Day-of-Week, Retour Reasons,
// Driver Ranking, Forecast, Rule-based Insights + Claude Sonnet AI summary.
// Recharts + Framer Motion animations.
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';
import {
  Sparkles, TrendingUp, TrendingDown, Minus, Package, AlertTriangle, Award, Users,
  Calendar, Activity, RefreshCw, Loader2, ChevronsUp, ChevronsDown, Zap, FileDown, User,
} from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const REASON_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];

function AnimatedCounter({ value, decimals = 0, duration = 1.2 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (value - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  const shown = decimals ? display.toFixed(decimals) : Math.round(display).toLocaleString();
  return <span>{shown}</span>;
}

function KPICard({ icon: Icon, label, value, suffix = '', decimals = 0, gradient, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative overflow-hidden rounded-xl p-4 shadow-sm ${gradient}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-white/80 uppercase tracking-wide">{label}</span>
        <Icon className="h-4 w-4 text-white/70" />
      </div>
      <div className="text-3xl font-bold text-white">
        <AnimatedCounter value={value || 0} decimals={decimals} />{suffix}
      </div>
    </motion.div>
  );
}

function YoyChip({ label, current, previous, delta, deltaPct, format = 'number', testId }) {
  const isPos = (deltaPct ?? delta) > 0;
  const isNeg = (deltaPct ?? delta) < 0;
  const Icon = isPos ? ChevronsUp : isNeg ? ChevronsDown : Minus;
  const color = isPos ? 'text-emerald-600' : isNeg ? 'text-rose-600' : 'text-gray-500';
  const bg = isPos ? 'bg-emerald-50' : isNeg ? 'bg-rose-50' : 'bg-gray-50';
  const fmt = (v) => format === 'pct' ? `%${v?.toFixed?.(2) ?? v}` : (v ?? 0).toLocaleString();
  const deltaText = deltaPct != null ? `${deltaPct >= 0 ? '+' : ''}${deltaPct}%` : (delta != null ? `${delta >= 0 ? '+' : ''}${delta}` : '—');
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`rounded-lg p-3 border ${bg}`}
      data-testid={testId}
    >
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-bold">{fmt(current)}</span>
        <span className="text-xs text-gray-400">/ {fmt(previous)}</span>
      </div>
      <div className={`flex items-center gap-1 text-sm font-semibold ${color}`}>
        <Icon className="h-3 w-3" />
        <span>{deltaText}</span>
      </div>
    </motion.div>
  );
}

function InsightItem({ item, i }) {
  const styles = {
    positive: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    info: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  };
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: i * 0.06 }}
      className={`rounded-lg border px-3 py-2 text-sm ${styles[item.type] || styles.info}`}
    >
      {item.message}
    </motion.div>
  );
}

export default function DPDAnalytics() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null); // { courier }
  const [driverData, setDriverData] = useState(null);
  const [driverLoading, setDriverLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfRef = useRef(null);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    setData(null); // Flicker'ı önle — eski veriyi tazelemeden gösterme
    try {
      // 1️⃣ Hızlı fetch — AI özeti hariç tüm veriler
      const r = await api.get(`/dpd/analytics/fast-trans?firm_id=${firmId}&year=${year}&ai_summary=false`);
      setData(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analitik yüklenemedi');
    } finally {
      setLoading(false);
    }
    // 2️⃣ Arka planda AI özetini yükle (~10-15 sn) — UI bloklanmaz
    setAiLoading(true);
    try {
      const r2 = await api.get(`/dpd/analytics/fast-trans/ai-summary?firm_id=${firmId}&year=${year}`);
      const ai = r2.data?.data?.ai_summary;
      if (ai) {
        setData((prev) => prev ? { ...prev, ai_summary: ai } : prev);
      }
    } catch (e) {
      // sessiz — AI opsiyonel
    } finally {
      setAiLoading(false);
    }
  }, [firmId, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDriver = async (courier) => {
    setSelectedDriver({ courier });
    setDriverData(null);
    setDriverLoading(true);
    try {
      // Fast: fetch KPIs/monthly/reasons without AI (~200ms)
      const r = await api.get(`/dpd/analytics/driver?firm_id=${firmId}&courier=${encodeURIComponent(courier)}&year=${year}&ai_summary=false`);
      setDriverData(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Şoför analizi yüklenemedi');
    } finally {
      setDriverLoading(false);
    }
    // Second pass: enrich with AI coaching (~10-15s), non-blocking UI
    try {
      const r2 = await api.get(`/dpd/analytics/driver?firm_id=${firmId}&courier=${encodeURIComponent(courier)}&year=${year}&ai_summary=true`);
      const ai = r2.data?.data?.ai_summary;
      if (ai) {
        setDriverData((prev) => prev ? { ...prev, ai_summary: ai } : prev);
      }
    } catch (e) {
      // silent
    }
  };

  const exportPdf = async () => {
    if (!pdfRef.current) return;
    setPdfLoading(true);
    try {
      const container = pdfRef.current;
      const sections = container.querySelectorAll('[data-pdf-section]');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 24;
      let cursorY = margin;
      // Title page header
      pdf.setFontSize(18); pdf.setTextColor(60, 40, 160);
      pdf.text(`${t('dpd.analytics.title')} · ${year}`, margin, cursorY + 12);
      pdf.setFontSize(10); pdf.setTextColor(120, 120, 120);
      pdf.text(new Date().toLocaleString(), margin, cursorY + 30);
      cursorY += 50;

      const targets = sections.length ? Array.from(sections) : [container];
      for (const el of targets) {
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
        const imgW = pageW - margin * 2;
        const imgH = (canvas.height * imgW) / canvas.width;
        if (cursorY + imgH > pageH - margin) {
          pdf.addPage();
          cursorY = margin;
        }
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        pdf.addImage(imgData, 'JPEG', margin, cursorY, imgW, imgH, undefined, 'FAST');
        cursorY += imgH + 14;
      }
      const fname = t('dpd.analytics.pdf.fileName', { year });
      pdf.save(fname);
      toast.success(t('dpd.analytics.pdf.done'));
    } catch (e) {
      console.error(e);
      toast.error(t('dpd.analytics.pdf.failed'));
    } finally {
      setPdfLoading(false);
    }
  };

  const years = [];
  const cy = new Date().getFullYear();
  for (let y = cy + 1; y >= cy - 4; y--) years.push(y);

  const kpi = data?.kpi;
  const yoy = data?.yoy;
  const monthly = data?.monthly || [];
  const dow = data?.day_of_week || [];
  const topReasons = data?.top_reasons || [];
  const reasonTrend = data?.reason_trend || [];
  const drivers = data?.driver_ranking || [];
  const forecast = data?.forecast;
  const insights = data?.insights || [];
  const aiSummary = data?.ai_summary;

  const isEmpty = !loading && data && (kpi?.total_packages === 0 && (yoy?.packages?.previous || 0) === 0);

  return (
    <div className="space-y-4" data-testid="dpd-analytics-page">
      {/* Header */}
      <Card className="border-indigo-100">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              {t('dpd.analytics.title')}
            </CardTitle>
            <CardDescription>{t('dpd.analytics.subtitle')}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger className="w-[180px]" data-testid="analytics-firm-select">
                <SelectValue placeholder={t('dpd.common.firm') || 'Firma'} />
              </SelectTrigger>
              <SelectContent>{firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger className="w-[110px]" data-testid="analytics-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={fetchData} variant="outline" disabled={loading} data-testid="analytics-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.analytics.refresh')}
            </Button>
            <Button onClick={exportPdf} disabled={pdfLoading || loading || !data} data-testid="analytics-pdf-btn">
              {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              {t('dpd.analytics.pdf.export')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : isEmpty ? (
        <Card><CardContent className="p-10 text-center text-gray-500">{t('dpd.analytics.empty')}</CardContent></Card>
      ) : data && (
        <div ref={pdfRef} className="space-y-4">
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-pdf-section>
            <KPICard icon={Package} label={t('dpd.analytics.kpi.packages')} value={kpi.total_packages}
              gradient="bg-gradient-to-br from-indigo-500 to-purple-600" testId="kpi-packages" />
            <KPICard icon={AlertTriangle} label={t('dpd.analytics.kpi.retour')} value={kpi.total_retour}
              gradient="bg-gradient-to-br from-rose-500 to-red-600" testId="kpi-retour" />
            <KPICard icon={Award} label={t('dpd.analytics.kpi.deliveryRate')} value={kpi.delivery_rate_pct} suffix="%" decimals={2}
              gradient="bg-gradient-to-br from-emerald-500 to-green-600" testId="kpi-delivery" />
            <KPICard icon={Activity} label={t('dpd.analytics.kpi.avgDaily')} value={kpi.avg_daily_packages} decimals={1}
              gradient="bg-gradient-to-br from-sky-500 to-cyan-600" testId="kpi-avg-daily" />
            <KPICard icon={Calendar} label={t('dpd.analytics.kpi.activeDays')} value={kpi.active_days}
              gradient="bg-gradient-to-br from-amber-500 to-orange-600" testId="kpi-active-days" />
            <KPICard icon={Users} label={t('dpd.analytics.kpi.activeCouriers')} value={kpi.active_couriers}
              gradient="bg-gradient-to-br from-fuchsia-500 to-pink-600" testId="kpi-active-couriers" />
            <KPICard icon={TrendingUp} label={t('dpd.analytics.kpi.activeTours')} value={kpi.active_tours}
              gradient="bg-gradient-to-br from-teal-500 to-emerald-600" testId="kpi-active-tours" />
            <KPICard icon={Zap} label={forecast ? t('dpd.analytics.forecast.nextMonthRetour') : t('dpd.analytics.forecast.title')}
              value={forecast?.next_month_retour_est || 0}
              gradient="bg-gradient-to-br from-slate-600 to-gray-700" testId="kpi-forecast" />
          </div>

          {/* YoY chips */}
          {/* YoY chips */}
          <Card data-pdf-section>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {t('dpd.analytics.yoy.title', { prev: yoy.packages.previous ? (year - 1) : '-', cur: year })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <YoyChip label={t('dpd.analytics.yoy.packages')} current={yoy.packages.current} previous={yoy.packages.previous}
                  deltaPct={yoy.packages.delta_pct} testId="yoy-packages" />
                <YoyChip label={t('dpd.analytics.yoy.retour')} current={yoy.retour.current} previous={yoy.retour.previous}
                  deltaPct={yoy.retour.delta_pct} testId="yoy-retour" />
                <YoyChip label={t('dpd.analytics.yoy.deliveryRate')} current={yoy.delivery_rate.current} previous={yoy.delivery_rate.previous}
                  delta={yoy.delivery_rate.delta} format="pct" testId="yoy-delivery" />
                <YoyChip label={t('dpd.analytics.yoy.couriers')} current={yoy.active_couriers.current} previous={yoy.active_couriers.previous}
                  delta={yoy.active_couriers.current - yoy.active_couriers.previous} testId="yoy-couriers" />
              </div>
            </CardContent>
          </Card>

          {/* AI Summary */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-pdf-section>
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-indigo-600" />{t('dpd.analytics.ai.title')}
                </CardTitle>
                <CardDescription>{t('dpd.analytics.ai.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent data-testid="ai-summary-content">
                {aiLoading ? (
                  <div className="space-y-2" data-testid="ai-summary-skeleton">
                    <div className="flex items-center gap-2 text-xs text-indigo-600 mb-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span className="font-medium">{t('dpd.analytics.ai.loading')}</span>
                    </div>
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-11/12" />
                    <Skeleton className="h-3 w-9/12" />
                    <Skeleton className="h-3 w-10/12" />
                  </div>
                ) : aiSummary ? (
                  <p className="text-sm text-gray-800 leading-relaxed">{aiSummary}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">{t('dpd.analytics.ai.error')}</p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Insights */}
          {insights.length > 0 && (
            <Card data-pdf-section>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('dpd.analytics.insights.title')}</CardTitle>
                <CardDescription>{t('dpd.analytics.insights.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="insights-list">
                  {insights.map((it, i) => <InsightItem key={i} item={it} i={i} />)}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Trend + Day of Week */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-pdf-section>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('dpd.analytics.monthly.title')}</CardTitle>
                <CardDescription>{t('dpd.analytics.monthly.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={monthly}>
                    <defs>
                      <linearGradient id="pkgGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area yAxisId="l" type="monotone" dataKey="packages" name={t('dpd.analytics.monthly.packages')}
                      stroke="#6366f1" fill="url(#pkgGrad)" strokeWidth={2} animationDuration={1200} />
                    <Area yAxisId="r" type="monotone" dataKey="retour" name={t('dpd.analytics.monthly.retour')}
                      stroke="#ef4444" fill="url(#retGrad)" strokeWidth={2} animationDuration={1200} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('dpd.analytics.dow.title')}</CardTitle>
                <CardDescription>{t('dpd.analytics.dow.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dow}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="label_short" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="l" dataKey="packages" name={t('dpd.analytics.monthly.packages')} fill="#6366f1" radius={[6, 6, 0, 0]} animationDuration={1200} />
                    <Line yAxisId="r" type="monotone" dataKey="delivery_pct" name={t('dpd.analytics.monthly.deliveryPct')} stroke="#10b981" strokeWidth={2} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Retour Reasons + Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-pdf-section>
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('dpd.analytics.reasons.topTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                {topReasons.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={topReasons} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={70}
                          animationDuration={1200}>
                          {topReasons.map((_, i) => <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v, n) => [`${v} (${((v / topReasons.reduce((s, r) => s + r.count, 0)) * 100).toFixed(1)}%)`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1 mt-2">
                      {topReasons.map((r, i) => (
                        <div key={r.code} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 truncate">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: REASON_COLORS[i % REASON_COLORS.length] }} />
                            <span className="truncate">{r.label}</span>
                          </div>
                          <span className="font-semibold">{r.share_pct}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-400 py-10 text-sm">{t('dpd.analytics.empty')}</div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('dpd.analytics.reasons.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart data={reasonTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {topReasons.map((r, i) => (
                      <Line
                        key={r.code}
                        type="monotone"
                        dataKey={`code_${r.code}`}
                        name={`${r.code} ${r.label.slice(0, 20)}`}
                        stroke={REASON_COLORS[i % REASON_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        animationDuration={1200}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Driver Ranking */}
          {drivers.length > 0 && (
            <Card data-pdf-section>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {t('dpd.analytics.drivers.title')}
                  <span className="text-xs font-normal text-gray-400">· {t('dpd.analytics.driver.clickHint')}</span>
                </CardTitle>
                <CardDescription>{t('dpd.analytics.drivers.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-x-auto">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">{t('dpd.analytics.drivers.rank')}</TableHead>
                        <TableHead>{t('dpd.analytics.drivers.courier')}</TableHead>
                        <TableHead className="text-right">{t('dpd.analytics.drivers.score')}</TableHead>
                        <TableHead className="text-right">{t('dpd.analytics.drivers.packages')}</TableHead>
                        <TableHead className="text-right">{t('dpd.analytics.drivers.retour')}</TableHead>
                        <TableHead className="text-right">{t('dpd.analytics.drivers.deliveryPct')}</TableHead>
                        <TableHead className="text-right">{t('dpd.analytics.drivers.days')}</TableHead>
                        <TableHead className="w-[280px]">{t('dpd.analytics.drivers.recommendation')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drivers.slice(0, 15).map((d) => (
                        <motion.tr
                          key={d.courier}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: (d.rank - 1) * 0.04 }}
                          className="border-b hover:bg-indigo-50 cursor-pointer"
                          data-testid={`driver-row-${d.rank}`}
                          onClick={() => openDriver(d.courier)}
                        >
                          <TableCell>
                            <Badge variant={d.rank <= 3 ? 'default' : 'outline'} className={d.rank === 1 ? 'bg-amber-500' : d.rank === 2 ? 'bg-gray-400' : d.rank === 3 ? 'bg-orange-500' : ''}>
                              #{d.rank}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{d.courier}</TableCell>
                          <TableCell className="text-right font-bold">{d.score}</TableCell>
                          <TableCell className="text-right">{d.packages.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-rose-600">{d.retour}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className={
                              d.delivery_pct >= 99 ? 'text-emerald-700 border-emerald-300' :
                                d.delivery_pct >= 95 ? 'text-amber-700 border-amber-300' :
                                  'text-rose-700 border-rose-300'
                            }>
                              {d.delivery_pct}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{d.active_days}</TableCell>
                          <TableCell className="text-xs text-gray-700">{d.recommendation}</TableCell>
                        </motion.tr>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Forecast card */}
          {forecast && (
            <Card data-pdf-section>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />{t('dpd.analytics.forecast.title')}
                </CardTitle>
                <CardDescription>{t('dpd.analytics.forecast.based', { n: forecast.based_on_months })}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border p-3 bg-amber-50">
                    <div className="text-xs text-gray-500">{t('dpd.analytics.forecast.nextMonthRetour')}</div>
                    <div className="text-2xl font-bold text-amber-700">
                      ~<AnimatedCounter value={forecast.next_month_retour_est} />
                    </div>
                  </div>
                  <div className="rounded-lg border p-3 bg-indigo-50">
                    <div className="text-xs text-gray-500">{t('dpd.analytics.forecast.nextMonthPackages')}</div>
                    <div className="text-2xl font-bold text-indigo-700">
                      ~<AnimatedCounter value={forecast.next_month_packages_est} />
                    </div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-gray-500">Trend</div>
                    <div className="text-xl font-semibold flex items-center gap-2">
                      {forecast.trend === 'up' && <><TrendingUp className="h-5 w-5 text-rose-600" />{t('dpd.analytics.forecast.trend.up')}</>}
                      {forecast.trend === 'down' && <><TrendingDown className="h-5 w-5 text-emerald-600" />{t('dpd.analytics.forecast.trend.down')}</>}
                      {forecast.trend === 'flat' && <><Minus className="h-5 w-5 text-gray-500" />{t('dpd.analytics.forecast.trend.flat')}</>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Driver Detail Modal */}
      <Dialog open={!!selectedDriver} onOpenChange={(o) => { if (!o) { setSelectedDriver(null); setDriverData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="driver-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-600" />
              {t('dpd.analytics.driver.modalTitle', { name: selectedDriver?.courier || '' })}
            </DialogTitle>
            <DialogDescription>{year}</DialogDescription>
          </DialogHeader>
          {driverLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            </div>
          ) : driverData?.empty ? (
            <div className="text-center text-gray-500 py-6">{t('dpd.analytics.empty')}</div>
          ) : driverData && (
            <div className="space-y-4">
              {/* Personal KPI */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <KPICard icon={Package} label={t('dpd.analytics.kpi.packages')} value={driverData.kpi.packages}
                  gradient="bg-gradient-to-br from-indigo-500 to-purple-600" testId="drv-kpi-pkg" />
                <KPICard icon={AlertTriangle} label={t('dpd.analytics.kpi.retour')} value={driverData.kpi.retour}
                  gradient="bg-gradient-to-br from-rose-500 to-red-600" testId="drv-kpi-ret" />
                <KPICard icon={Award} label={t('dpd.analytics.kpi.deliveryRate')} value={driverData.kpi.delivery_pct} suffix="%" decimals={2}
                  gradient="bg-gradient-to-br from-emerald-500 to-green-600" testId="drv-kpi-dp" />
                <KPICard icon={Calendar} label={t('dpd.analytics.kpi.activeDays')} value={driverData.kpi.active_days}
                  gradient="bg-gradient-to-br from-amber-500 to-orange-600" testId="drv-kpi-days" />
              </div>

              {/* Peer comparison */}
              {driverData.peer && driverData.peer.rank && (
                <Card>
                  <CardContent className="p-3 flex items-center justify-between gap-4">
                    <div>
                      <div className="text-xs text-gray-500">{t('dpd.analytics.driver.rank')}</div>
                      <div className="text-2xl font-bold text-indigo-700">
                        {driverData.peer.rank} / {driverData.peer.total}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{t('dpd.analytics.driver.vsAvg')}</div>
                      <div className={`text-lg font-bold ${driverData.peer.delta_vs_avg >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {driverData.peer.delta_vs_avg >= 0 ? '+' : ''}{driverData.peer.delta_vs_avg} puan
                      </div>
                      <div className="text-xs text-gray-400">Firma ort. %{driverData.peer.firm_avg_delivery_pct}</div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI coaching */}
              <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-indigo-600" />
                    {t('dpd.analytics.driver.coaching')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {driverData.ai_summary ? (
                    <p className="text-sm text-gray-800 leading-relaxed" data-testid="driver-ai-coaching">{driverData.ai_summary}</p>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('dpd.analytics.ai.loading')}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Monthly personal trend */}
              {driverData.monthly?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('dpd.analytics.driver.personalTrend')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={driverData.monthly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar yAxisId="l" dataKey="packages" name={t('dpd.analytics.monthly.packages')} fill="#6366f1" radius={[4, 4, 0, 0]} animationDuration={800} />
                        <Bar yAxisId="l" dataKey="retour" name={t('dpd.analytics.monthly.retour')} fill="#ef4444" radius={[4, 4, 0, 0]} animationDuration={800} />
                        <Line yAxisId="r" type="monotone" dataKey="delivery_pct" name={t('dpd.analytics.monthly.deliveryPct')} stroke="#10b981" strokeWidth={2} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Personal top retour reasons */}
              {driverData.top_reasons?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t('dpd.analytics.driver.yourReasons')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {driverData.top_reasons.map((r, i) => (
                        <div key={r.code} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 truncate">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: REASON_COLORS[i % REASON_COLORS.length] }} />
                            <span className="truncate">{r.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{r.count}</span>
                            <span className="text-xs text-gray-400">{r.share_pct}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
